import { DialogTitle } from "@mui/material";
import { orderBusiness } from "api/bussiness/order/order-bussiness";
import { packingNoteBusiness } from "api/bussiness/package-note/packing-note-business";
import { workOrderPoBusiness } from "api/bussiness/work-order-po/work-order-po-bussiness";
import { workOrderBusiness } from "api/bussiness/work-order/work-order-bussiness";
import { dateTimeToUTCString } from "commons/ultilities/date/dayjs";
import { DynamicForm } from "commons/ultilities/dynamic-form/components/dynamic-form";
import { DynamicFormArray } from "commons/ultilities/dynamic-form/components/dynamic-form-array";
import { useForm } from "commons/ultilities/dynamic-form/hooks/use-form";
import {
  IControl,
  IControlProperties,
} from "commons/ultilities/dynamic-form/models/control";
import { createProductOptionLabel } from "commons/ultilities/option-item/label-ultil";
import dayjs from "dayjs";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react-lite";
import { IOrderProperties } from "mobx-stores/_models/orders/order";
import { IUpdatePackingNote } from "mobx-stores/_models/packing-note/packing-note";
import { IProductProperties } from "mobx-stores/_models/products/product";
import { IWorkOrderPoProperties } from "mobx-stores/_models/work-order-po/work-order-po";
import { IWorkOrderProperties } from "mobx-stores/_models/work-order/work-order";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { DialogComplexContent } from "share-components/templates/form/dialog-complex-form/dialog-complex-content";
import { DialogComplexFormContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form";
import { DialogComplexFormArrayContainer } from "share-components/templates/form/dialog-complex-form/dialog-complex-form-array";
import { DialogForm } from "share-components/templates/form/dialog-form";
import { getEditPackingNoteFormSchema } from "../schema";

export const EditPackingNote = observer(() => {
  const {
    packingNoteManagement: {
      openEdit,
      getPackingNoteDetail,
      setProp,
      packingNoteDetail,
    },
  } = useStores();
  const {
    form: { controls, isValid },
    getControl,
    getControlArray,
    setDefaults,
    submitHandler,
  } = useForm<IUpdatePackingNote>(
    (form) => getEditPackingNoteFormSchema(form),
    {
      id: "",
      date: dateTimeToUTCString(dayjs()),
      number: "",
      kcsPackage: "",
      kcsTest: "",
      workOrderId: "",
      workOrderPOId: "",
      customerId: "",
      purchaseOrderId: "",
      items: [],
    }
  );

  const itemControl = useMemo(() => {
    return getControl("items");
  }, [getControl]);

  const setOptionItemsForProduct = useCallback(
    (wo: IWorkOrderProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        productIdSchema.optionItems = wo.items?.map((x) => ({
          label: createProductOptionLabel(x.product as IProductProperties),
          value: x.product?.id,
        }));
        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl]
  );

  const setOptionItemsForProductFromWorkOrderPo = useCallback(
    (wo: IWorkOrderPoProperties) => {
      if (itemControl) {
        const newChildSchema = cloneDeep(itemControl.childSchema);
        const productIdSchema = newChildSchema.productId as IControlProperties;
        const product = wo.purchaseOrderItem?.product;
        productIdSchema.optionItems = product
          ? [
              {
                label: createProductOptionLabel(product as IProductProperties),
                value: product.id,
              },
            ]
          : [];

        itemControl.setChildSchema(newChildSchema);
      }
    },
    [itemControl]
  );

  const setOptionItemsSchemaForProduct = useCallback(async () => {
    if (!packingNoteDetail) return;

    const hasWorkOrder = !!packingNoteDetail.workOrderId;
    const hasWorkOrderPO = !!packingNoteDetail.workOrderPOId;
    if (!hasWorkOrder && !hasWorkOrderPO) return;

    if (hasWorkOrderPO) {
      const woDetail = await workOrderPoBusiness.getById(
        packingNoteDetail.workOrderPOId!,
      );
      setOptionItemsForProductFromWorkOrderPo(woDetail);

      if (packingNoteDetail.items && packingNoteDetail.items.length > 0) {
        const product = woDetail.purchaseOrderItem?.product;
        const productOption = product
          ? [
              {
                value: product.id,
                label: createProductOptionLabel(product as IProductProperties),
              },
            ]
          : [];
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = packingNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp("optionItems", productOption);
          }
        });
      }
      return;
    }

    if (hasWorkOrder) {
      const woDetail = await workOrderBusiness.getById(
        packingNoteDetail.workOrderId!,
      );
      setOptionItemsForProduct(woDetail);

      if (packingNoteDetail.items && packingNoteDetail.items.length > 0) {
        getControl("items").children.forEach((row: IControl[]) => {
          const idCtr = row.filter((x) => x.name === "id")?.[0];
          const data = packingNoteDetail.items.filter(
            (x) => idCtr && x.id === idCtr.value
          )?.[0];
          const productIdCtr = row.filter((x) => x.name === "productId")?.[0];
          if (productIdCtr && data.product) {
            productIdCtr.setProp(
              "optionItems",
              woDetail.items?.map((x) => ({
                value: x.product?.id,
                label: createProductOptionLabel(x.product as IProductProperties),
              }))
            );
          }
        });
      }
    }
  }, [getControl, packingNoteDetail, setOptionItemsForProduct, setOptionItemsForProductFromWorkOrderPo]);

  const setOptionItemsForProductIsEmty = useCallback(() => {
    if (itemControl) {
      const newChildSchema = cloneDeep(itemControl.childSchema);
      const productIdSchema = newChildSchema.productId as IControlProperties;
      productIdSchema.optionItems = [];

      itemControl.setChildSchema(newChildSchema);
    }
  }, [itemControl]);

  useEffect(() => {
    if (packingNoteDetail) {
      setDefaults({
        id: packingNoteDetail.id,
        date: packingNoteDetail.date,
        number: packingNoteDetail.number,
        kcsPackage: packingNoteDetail.kcsPackage,
        purchaseOrderId: packingNoteDetail.purchaseOrderId,
        kcsTest: packingNoteDetail.kcsTest,
        workOrderId: packingNoteDetail.workOrderId,
        workOrderPOId: packingNoteDetail.workOrderPOId,
        customerId: packingNoteDetail.customerId,
        items: packingNoteDetail.items.map((x) => ({
          id: x.id,
          amount: x.amount,
          boxNumber: x.boxNumber,
          netWeight: x.netWeight,
          orderNumber: x.orderNumber,
          grossWeight: x.grossWeight,
          productId: x.productId,
          expiredDate: x.expiredDate,
        })),
      });

      setOptionItemsSchemaForProduct();
    }
  }, [
    getControl,
    packingNoteDetail,
    setDefaults,
    setOptionItemsSchemaForProduct,
  ]);

  useEffect(() => {
    const customerCtr = getControl("purchaseOrderId");
    customerCtr.setProp("optionItems", [
      {
        value: packingNoteDetail?.purchaseOrderId,
        label: `${packingNoteDetail?.purchaseOrder?.poNumber || ""}`,
      },
    ]);
  }, [
    packingNoteDetail?.purchaseOrder?.poNumber,
    packingNoteDetail?.purchaseOrderId,
    getControl,
  ]);

  useEffect(() => {
    const workOrderPoCtr = getControl("workOrderPOId");
    workOrderPoCtr.setProp("optionItems", [
      {
        value: packingNoteDetail?.workOrderPOId,
        label: `${
          packingNoteDetail?.workOrderPO?.woNumber ||
          packingNoteDetail?.workOrder?.woNumber ||
          ""
        }`,
      },
    ]);
  }, [
    getControl,
    packingNoteDetail?.workOrderPO?.woNumber,
    packingNoteDetail?.workOrderPOId,
    packingNoteDetail?.workOrder?.woNumber,
  ]);

  useEffect(() => {
    const customerSelection = getControl("customerId");
    customerSelection.setProp("optionItems", [
      {
        label: packingNoteDetail?.customer?.dealingName || "",
        value: packingNoteDetail?.customer?.id,
      },
    ]);
  }, [
    getControl,
    packingNoteDetail?.customer?.id,
    packingNoteDetail?.customer?.dealingName,
  ]);

  const saveHandler = useCallback(
    async (data: IUpdatePackingNote) => {
      try {
        if (!packingNoteDetail?.id) return;
        await packingNoteBusiness.update(packingNoteDetail?.id, data);
        await getPackingNoteDetail(packingNoteDetail?.id);
        setProp("openEdit", false);
      } catch (ex) {
        console.error(ex);
      }
    },
    [getPackingNoteDetail, packingNoteDetail?.id, setProp]
  );

  const packingNoteItems = useMemo(() => {
    return getControlArray("items");
  }, [getControlArray]);

  const removeRowHandler = useCallback(
    (idx: number) => {
      itemControl.removeChildByIndex(idx);
    },
    [itemControl]
  );

  const addNewPackingNoteItemRow = useCallback(() => {
    itemControl.addNewChild();
  }, [itemControl]);

  const autoFillCustomerByPurchaseOrder = useCallback(
    (purchaseOrder: IOrderProperties) => {
      const customerIdCtr = getControl("customerId");
      if (!customerIdCtr) return;
      customerIdCtr.setValueAndValidate(purchaseOrder.customer?.id);
      customerIdCtr.setProp("optionItems", [
        {
          label: purchaseOrder.customer?.dealingName || "",
          value: purchaseOrder.customer?.id,
        },
      ]);
    },
    [getControl]
  );

  const cleanItems = useCallback(() => {
    if (itemControl) {
      itemControl.setProp("children", []);
    }
  }, [itemControl]);

  const cleanWOValue = useCallback(() => {
    const woCtr = getControl("workOrderPOId");
    if (woCtr) {
      woCtr.setDefaultValue(null);
    }
  }, [getControl]);

  const valueChangeHandler = useCallback(
    async (control: IControl) => {
      if (control.name === "purchaseOrderId") {
        if (control.value) {
          const purchaseOrderDetail = await orderBusiness.getById(
            control.value
          );
          autoFillCustomerByPurchaseOrder(purchaseOrderDetail);
          cleanWOValue();
          cleanItems();
          setOptionItemsForProductIsEmty();
          itemControl.addNewChild();
        }
      }

      if (control.name === "workOrderPOId") {
        if (control.value) {
          cleanItems();
          setOptionItemsForProductIsEmty();
          const wo = await workOrderPoBusiness.getById(control.value);
          setOptionItemsForProductFromWorkOrderPo(wo);

          itemControl.addNewChild();
        }
      }
    },
    [
      autoFillCustomerByPurchaseOrder,
      cleanItems,
      cleanWOValue,
      itemControl,
      setOptionItemsForProductFromWorkOrderPo,
      setOptionItemsForProductIsEmty,
    ]
  );

  return (
    <DialogForm
      open={openEdit}
      fullWidth
      maxWidth={"xl"}
      onClose={() => setProp("openEdit", false)}
      onCancelBtnClicked={() => setProp("openEdit", false)}
      onSaveBtnClick={async () => await submitHandler(saveHandler)}
      saveBtnProps={{
        disabled: !isValid,
      }}
    >
      <DialogTitle>Sửa phiếu đóng gói</DialogTitle>
      <DialogComplexContent>
        <DialogComplexFormContainer>
          <DynamicForm
            controls={controls}
            onValueChanged={valueChangeHandler}
          ></DynamicForm>
        </DialogComplexFormContainer>
        <DialogComplexFormArrayContainer
          label={itemControl.label}
          addNewRow={addNewPackingNoteItemRow}
        >
          {packingNoteItems && packingNoteItems.length > 0 && (
            <DynamicFormArray
              containerProps={{
                sx: {
                  marginTop: "10px",
                  marginBottom: "10px",
                },
              }}
              children={packingNoteItems}
              onRemoveRowClick={removeRowHandler}
            ></DynamicFormArray>
          )}
        </DialogComplexFormArrayContainer>
      </DialogComplexContent>
    </DialogForm>
  );
});
